import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, buttonClasses, Card, IconX, MicroLabel } from "@bht/ui";
import {
  fetchMe,
  logout,
  setTarget,
  type ExamTarget,
  type Me,
  type ScoreBand,
} from "../lib/api.js";
import { friendlyError } from "../lib/errors.js";
import { clearSession } from "../lib/session.js";
import { TargetPicker } from "./TargetPicker.js";
import { InstallApp } from "./InstallApp.js";

export type ProfileDrawerProps = {
  open: boolean;
  onClose: () => void;
};

/**
 * Slide-in drawer from the right (desktop) / bottom sheet (mobile)
 * triggered by tapping the avatar bubble in AppHeader.
 *
 * Shows:
 *   - User identity (name, email, picture)
 *   - Training for: <exams> · <band> · <date>  + Edit button
 *   - Theme toggle (moved here from header)
 *   - Sign out
 *
 * Edit button opens the Goal Switch modal in-drawer using TargetPicker.
 */
export function ProfileDrawer({ open, onClose }: ProfileDrawerProps) {
  const [me, setMe] = useState<Me | null>(null);
  const [editing, setEditing] = useState(false);
  const [draftExams, setDraftExams] = useState<ExamTarget[]>([]);
  const [draftBand, setDraftBand] = useState<ScoreBand | null>(null);
  const [draftDate, setDraftDate] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    fetchMe()
      .then((r) => {
        setMe(r);
        setDraftExams(r.target_exams);
        setDraftBand(r.target_band);
        setDraftDate(r.target_date);
      })
      .catch(() => undefined);
  }, [open]);

  function onLogout() {
    logout().catch(() => undefined);
    clearSession();
    window.location.href = "/signin";
  }

  async function saveEdit() {
    if (draftExams.length === 0) {
      setError("Pick at least one exam.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const r = await setTarget({
        target_exams: draftExams,
        target_band: draftBand,
        target_date: draftDate,
      });
      setMe((prev) =>
        prev
          ? {
              ...prev,
              target_exams: r.target_exams,
              target_band: r.target_band,
              target_date: r.target_date,
            }
          : prev,
      );
      setEditing(false);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <>
      {/* Scrim */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "var(--bg-scrim)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          zIndex: 100,
        }}
      />

      {/* Drawer */}
      <div
        role="dialog"
        aria-label="Profile"
        style={{
          position: "fixed",
          right: 0,
          top: 0,
          bottom: 0,
          width: "min(420px, 90vw)",
          background: "var(--bg-0)",
          borderLeft: "1px solid var(--border-line)",
          zIndex: 101,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            padding: "20px 20px 16px",
            borderBottom: "1px solid var(--border-faint)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <MicroLabel>Profile</MicroLabel>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close profile"
            style={{
              background: "none",
              border: "none",
              color: "var(--fg-2)",
              cursor: "pointer",
              padding: 4,
              lineHeight: 0,
            }}
          >
            <IconX size={18} />
          </button>
        </div>

        <div className="app-stack" style={{ padding: 20, gap: "var(--sp-5)" }}>
          {/* Identity */}
          {me && (
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <Avatar user={me} size={48} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    color: "var(--fg-0)",
                    fontWeight: 500,
                    fontSize: 16,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {me.name || me.email?.split("@")[0] || "Your account"}
                </div>
                <div
                  style={{
                    color: "var(--fg-3)",
                    fontSize: 13,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {me.email}
                </div>
              </div>
            </div>
          )}

          {/* Training for */}
          {me && !editing && (
            <Card>
              <div className="app-stack" style={{ gap: "var(--sp-2)" }}>
                <MicroLabel>Training for</MicroLabel>
                <div
                  style={{
                    color: "var(--fg-0)",
                    fontSize: "var(--t-body)",
                    fontWeight: 500,
                    lineHeight: 1.4,
                  }}
                >
                  {me.target_exams.join(" · ") || "(not set)"}
                  {me.target_band && (
                    <span
                      style={{
                        color: "var(--accent-amber)",
                        marginLeft: 8,
                        fontFamily: "var(--font-mono)",
                        fontSize: 13,
                      }}
                    >
                      {me.target_band}
                    </span>
                  )}
                </div>
                {me.target_date && (
                  <div
                    style={{
                      color: "var(--fg-3)",
                      fontSize: 12,
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    Test: {me.target_date}
                  </div>
                )}
                <Button
                  variant="ghost"
                  full
                  onClick={() => setEditing(true)}
                  style={{ marginTop: 4 }}
                >
                  Edit target
                </Button>
              </div>
            </Card>
          )}

          {me && editing && (
            <Card>
              <div className="app-stack" style={{ gap: "var(--sp-4)" }}>
                <MicroLabel>Change target</MicroLabel>
                <TargetPicker
                  exams={draftExams}
                  band={draftBand}
                  date={draftDate}
                  onExamsChange={setDraftExams}
                  onBandChange={setDraftBand}
                  onDateChange={setDraftDate}
                />
                {error && (
                  <p
                    style={{
                      color: "var(--signal-red)",
                      fontSize: "var(--t-small)",
                      margin: 0,
                    }}
                  >
                    {error}
                  </p>
                )}
                <div className="app-row" style={{ gap: "var(--sp-3)" }}>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setEditing(false);
                      setError(null);
                      setDraftExams(me.target_exams);
                      setDraftBand(me.target_band);
                      setDraftDate(me.target_date);
                    }}
                    style={{ flex: 1 }}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={saveEdit}
                    style={{ flex: 1 }}
                    disabled={saving || draftExams.length === 0}
                  >
                    {saving ? "Saving…" : "Save"}
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Theme toggle moved to the header — discoverable in one tap. */}

          {/* Admin dashboard — only rendered for the admin role (server is the
              real gate at /admin/*; this is just a convenience shortcut so the
              hidden /app/admin URL doesn't have to be remembered). */}
          {me?.is_admin && (
            <Link
              to="/app/admin"
              onClick={onClose}
              style={{ textDecoration: "none" }}
             className={buttonClasses({ variant: "ghost", full: true })}>Admin dashboard</Link>
          )}

          {/* Install. Renders nothing when already installed, or when the
              browser has not offered a prompt and is not iOS. */}
          <InstallApp />

          {/* Sign out */}
          <Button variant="ghost" full onClick={onLogout}>
            Sign out
          </Button>
        </div>
      </div>
    </>
  );
}

function Avatar({ user, size = 32 }: { user: Me; size?: number }) {
  if (user.picture_url) {
    return (
      <img
        src={user.picture_url}
        alt=""
        width={size}
        height={size}
        style={{
          borderRadius: "50%",
          objectFit: "cover",
          display: "block",
          flexShrink: 0,
        }}
      />
    );
  }
  // Both fields can be absent on a session restored from an older token, and
  // indexing undefined threw in production: "(e.name??e.email)[0]".
  const initial = (user.name || user.email || "").trim()[0]?.toUpperCase() ?? "?";
  return (
    <div
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: "var(--bg-2)",
        border: "1px solid var(--border-line)",
        color: "var(--accent-amber)",
        fontFamily: "var(--font-mono)",
        fontWeight: 600,
        fontSize: size * 0.45,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {initial}
    </div>
  );
}

/**
 * Just the avatar — used by AppHeader as the bubble that opens the
 * drawer. Lazy-fetches /me so the header renders fast even before the
 * me query completes.
 */
export function ProfileAvatarButton({
  onClick,
  size = 32,
}: {
  onClick: () => void;
  size?: number;
}) {
  const [me, setMe] = useState<Me | null>(null);
  useEffect(() => {
    fetchMe()
      .then(setMe)
      .catch(() => undefined);
  }, []);
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open profile"
      style={{
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Avatar keeps its size; the tap area around it clears 44px.
        minWidth: 44,
        minHeight: 44,
      }}
    >
      {me ? (
        <Avatar user={me} size={size} />
      ) : (
        <div
          style={{
            width: size,
            height: size,
            borderRadius: "50%",
            background: "var(--bg-2)",
            border: "1px solid var(--border-line)",
          }}
        />
      )}
    </button>
  );
}
